import {
  FormEvent,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import type {
  DocumentIntelligenceHistoryItem,
  DocumentIntelligenceProject,
  DocumentIntelligenceSourceMode,
  DocumentIntelligenceThread,
  DocumentIntelligenceWorkflow,
} from "../../shared/types";
import "./operations.css";

const workflowFilters = ["All", "Published", "Unpublished", "Created by me"];

const MAX_ATTACHED_FILES = 50;

export const fileSelectionKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

/**
 * Adds newly chosen files to an existing selection instead of replacing it.
 * Re-choosing the same file is a no-op rather than a duplicate, and the
 * selection is capped so a stray multi-select cannot grow without bound.
 */
export function mergeFileSelection(current: File[], incoming: File[]): File[] {
  const next = new Map(current.map((file) => [fileSelectionKey(file), file]));
  incoming.forEach((file) => next.set(fileSelectionKey(file), file));
  return [...next.values()].slice(0, MAX_ATTACHED_FILES);
}

export interface WorkflowsScreenProps {
  workflows: DocumentIntelligenceWorkflow[];
  busy?: boolean;
  onCreate: () => void;
  onOpen: (workflow: DocumentIntelligenceWorkflow) => void;
  onRun: (workflow: DocumentIntelligenceWorkflow) => void;
}

export function WorkflowsScreen({
  workflows,
  busy = false,
  onCreate,
  onOpen,
  onRun,
}: WorkflowsScreenProps) {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const visible = useMemo(() => workflows.filter((workflow) => {
    const status = (workflow.status || (workflow.custom ? "Unpublished" : "Published")).toLowerCase();
    const matchesFilter = filter === "All"
      || (filter === "Published" && status === "published")
      || (filter === "Unpublished" && status !== "published")
      || (filter === "Created by me" && !!workflow.can_edit);
    const haystack = `${workflow.name} ${workflow.description || ""} ${workflow.category || ""}`.toLowerCase();
    return matchesFilter && haystack.includes(query.toLowerCase());
  }), [filter, query, workflows]);

  return (
    <section className="dio-screen" aria-labelledby="dio-workflows-title">
      <div className="dio-heading-row">
        <div>
          <h1 id="dio-workflows-title">Workflows</h1>
          <p>Build repeatable legal work, publish it to your team and run it against an exact source set.</p>
        </div>
        <button type="button" className="dio-btn dio-btn--primary" onClick={onCreate} disabled={busy}>
          Create workflow
        </button>
      </div>
      <ol className="dio-workflow-guide" aria-label="How workflows work">
        <li><strong>1. Build</strong><span>Define the required inputs, AI actions and final response.</span></li>
        <li><strong>2. Test</strong><span>Run the draft with a real document and inspect the cited output.</span></li>
        <li><strong>3. Share</strong><span>Save privately or submit it for workspace approval.</span></li>
      </ol>

      <div className="dio-filterbar">
        <div className="dio-tabs" role="tablist" aria-label="Workflow filters">
          {workflowFilters.map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={filter === item}
              className={filter === item ? "is-active" : ""}
              onClick={() => setFilter(item)}
            >
              {item}
            </button>
          ))}
        </div>
        <label className="dio-search">
          <span className="dio-sr-only">Search workflows</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search workflows" />
        </label>
      </div>

      <div className="dio-table-wrap">
        <table className="dio-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Creator</th>
              <th>Last updated</th>
              <th>Build access</th>
              <th>Run access</th>
              <th>Runs</th>
              <th>Status</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {visible.map((workflow) => (
              <tr key={workflow.id || workflow.key}>
                <td>
                  <button type="button" className="dio-link" onClick={() => onOpen(workflow)}>
                    {workflow.name}
                  </button>
                </td>
                <td>{workflow.creator || (workflow.custom ? "You" : "KontractAI")}</td>
                <td>{workflow.updated_at ? new Date(workflow.updated_at).toLocaleDateString() : "Available now"}</td>
                <td>{workflow.can_edit ? "Me" : "Workspace"}</td>
                <td>{workflow.visibility === "personal" ? "Me" : "Workspace"}</td>
                <td>{workflow.run_count ?? "—"}</td>
                <td><span className="dio-tag">{workflow.status || (workflow.custom ? "Unpublished" : "Published")}</span></td>
                <td><button type="button" className="dio-btn dio-btn--small" onClick={() => onRun(workflow)}>Run</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!visible.length ? (
          <div className="dio-empty">
            <strong>No workflows match this view.</strong>
            <span>Clear the search or create a workflow for this workspace.</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

type BuilderPhase = "pick" | "questions" | "built";

export interface WorkflowBlock {
  id: string;
  kind: "User input" | "AI action" | "Response";
  name: string;
  output: string;
  prompt?: string;
}

const builderQuestions = [
  {
    question: "What type of document will this workflow review?",
    options: ["Commercial or procurement contract", "General corporate document", "IP litigation document"],
  },
  {
    question: "What should the review focus on?",
    options: ["Flag risks against our standard concerns", "Compare against a playbook", "Summarise key provisions"],
  },
  {
    question: "How should the results be delivered?",
    options: ["A written memo summarising the findings", "An issues table with risk levels", "A summary plus redline language"],
  },
];

const initialBlocks: WorkflowBlock[] = [
  { id: "contract", kind: "User input", name: "Contract upload", output: "contract_upload" },
  { id: "playbook", kind: "User input", name: "Playbook upload", output: "playbook_upload" },
  { id: "compare", kind: "AI action", name: "Playbook comparison", output: "playbook_comparison", prompt: "Review @contract_upload against @playbook_upload clause by clause." },
  { id: "redlines", kind: "AI action", name: "Summary and redlines", output: "summary_and_redlines", prompt: "Prepare a cited summary and proposed drafting from @playbook_comparison." },
  { id: "results", kind: "Response", name: "Results", output: "playbook_comparison + summary_and_redlines" },
];

export interface WorkflowBuilderSaveInput {
  name: string;
  type: string;
  answers: string[];
  blocks: WorkflowBlock[];
}

export interface WorkflowBuilderScreenProps {
  initialWorkflow?: DocumentIntelligenceWorkflow | null;
  busy?: boolean;
  onBack: () => void;
  onSave?: (input: WorkflowBuilderSaveInput) => Promise<void> | void;
  onPublish?: (input: WorkflowBuilderSaveInput) => Promise<void> | void;
  onTest?: (input: WorkflowBuilderSaveInput) => Promise<void> | void;
  publishLabel?: string;
}

export function WorkflowBuilderScreen({
  initialWorkflow = null,
  busy = false,
  onBack,
  onSave,
  onPublish,
  onTest,
  publishLabel = "Submit for approval",
}: WorkflowBuilderScreenProps) {
  const [phase, setPhase] = useState<BuilderPhase>(initialWorkflow ? "built" : "pick");
  const [type, setType] = useState(initialWorkflow?.category || "");
  const [question, setQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>(["", "", ""]);
  const [name, setName] = useState(initialWorkflow?.name || "Commercial contract playbook review");
  const [blocks, setBlocks] = useState<WorkflowBlock[]>(initialBlocks);
  const [selected, setSelected] = useState<string | null>(null);
  const [menu, setMenu] = useState<string | null>(null);
  const selectedBlock = blocks.find((block) => block.id === selected) || null;

  const payload = (): WorkflowBuilderSaveInput => ({ name, type, answers, blocks });
  const answer = (value: string) => {
    const next = [...answers];
    next[question] = value;
    setAnswers(next);
    if (question === builderQuestions.length - 1) setPhase("built");
    else setQuestion((current) => current + 1);
  };

  const updateSelected = (patch: Partial<WorkflowBlock>) => {
    if (!selected) return;
    setBlocks((current) => current.map((block) => block.id === selected ? { ...block, ...patch } : block));
  };

  return (
    <section className="dio-builder" aria-label="Workflow builder">
      <div className={`dio-builder__conversation ${selectedBlock ? "is-collapsed" : ""}`}>
        <button type="button" className="dio-back" onClick={onBack}>‹ All workflows</button>
        {phase === "pick" ? (
          <div className="dio-builder__phase">
            <span className="dio-kicker">Choose a starting point</span>
            <h1>What should this workflow do?</h1>
            <div className="dio-type-grid">
              {[
                ["review", "Review workflow", "Analyse or review a document"],
                ["redlining", "Redlining workflow", "Review a redline or markup"],
                ["drafting", "Drafting workflow", "Draft or generate a document"],
              ].map(([key, title, description]) => (
                <button key={key} type="button" onClick={() => { setType(key); setPhase("questions"); }}>
                  <strong>{title}</strong>
                  <span>{description}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {phase === "questions" ? (
          <div className="dio-builder__phase dio-builder__question">
            <div className="dio-user-line">Create a workflow to {type === "drafting" ? "draft a document" : type === "redlining" ? "review a redline" : "analyse or review a document"}.</div>
            <div className="dio-plan-copy">
              I will ask three questions, then build the input, analysis and response steps for you to inspect.
            </div>
            <div className="dio-question-card">
              <span className="dio-kicker">Question {question + 1} of 3</span>
              <h2>{builderQuestions[question].question}</h2>
              {builderQuestions[question].options.map((option, index) => (
                <button key={option} type="button" onClick={() => answer(option)}>
                  <span>{index + 1}</span>
                  <strong>{option}</strong>
                  {index === 0 ? <em>Recommended</em> : null}
                </button>
              ))}
              <div className="dio-question-actions">
                <button type="button" className="dio-btn" disabled={question === 0} onClick={() => setQuestion((current) => Math.max(0, current - 1))}>Previous</button>
                <button type="button" className="dio-btn" onClick={() => answer("DI decided")}>Skip</button>
              </div>
            </div>
          </div>
        ) : null}

        {phase === "built" && !selectedBlock ? (
          <div className="dio-builder__phase">
            <div className="dio-answer-summary">
              {builderQuestions.map((item, index) => (
                <div key={item.question}><span>{item.question}</span><strong>{answers[index] || "Saved workflow definition"}</strong></div>
              ))}
            </div>
            <h2>I have built the {name} workflow.</h2>
            <ol className="dio-builder__explanation">
              {blocks.map((block) => <li key={block.id}><strong>{block.name}</strong> — {block.kind === "AI action" ? "uses earlier outputs to produce grounded analysis." : block.kind === "Response" ? "presents the cited result." : "collects a required file from the user."}</li>)}
            </ol>
          </div>
        ) : null}
      </div>

      <div className="dio-builder__canvas">
        <header>
          <div>
            <input aria-label="Workflow name" value={name} onChange={(event) => setName(event.target.value)} />
            <span>Draft · {blocks.length} steps · estimated 3–5 minutes</span>
          </div>
          <div>
            <button type="button" className="dio-btn" disabled={!onTest || busy || phase !== "built"} title={!onTest ? "Workflow testing is not configured." : undefined} onClick={() => { void onTest?.(payload()); }}>Test</button>
            <button type="button" className="dio-btn" disabled={!onSave || busy || phase !== "built"} onClick={() => { void onSave?.(payload()); }}>Save</button>
            <button type="button" className="dio-btn dio-btn--primary" disabled={!onPublish || busy || phase !== "built"} onClick={() => { void onPublish?.(payload()); }}>{publishLabel}</button>
          </div>
        </header>

        {phase !== "built" ? (
          <div className="dio-empty dio-builder__empty"><strong>Start building your workflow</strong><span>Answer the three questions and the block canvas will appear here.</span></div>
        ) : (
          <div className="dio-flow">
            {blocks.map((block, index) => (
              <div key={block.id} className="dio-flow__item">
                <button
                  type="button"
                  className={`dio-block ${selected === block.id ? "is-selected" : ""}`}
                  onClick={() => { setSelected(block.id); setMenu(null); }}
                >
                  <span className="dio-tag">{block.kind}</span>
                  <strong>{block.name}</strong>
                  <small>Output <code>{block.output}</code></small>
                </button>
                <button type="button" className="dio-block-menu" aria-label={`Actions for ${block.name}`} onClick={() => setMenu(menu === block.id ? null : block.id)}>•••</button>
                {menu === block.id ? (
                  <div className="dio-block-dropdown">
                    <button type="button" disabled={index === 0} onClick={() => {
                      setBlocks((current) => {
                        const next = [...current];
                        [next[index - 1], next[index]] = [next[index], next[index - 1]];
                        return next;
                      });
                      setMenu(null);
                    }}>Move block up</button>
                    <button type="button" onClick={() => {
                      setBlocks((current) => current.filter((item) => item.id !== block.id));
                      setSelected(null);
                      setMenu(null);
                    }}>Delete step</button>
                  </div>
                ) : null}
                {index < blocks.length - 1 ? <span className="dio-connector" /> : null}
              </div>
            ))}
            <button type="button" className="dio-btn dio-add-step" onClick={() => {
              const id = `step-${Date.now()}`;
              setBlocks((current) => [...current, { id, kind: "AI action", name: "New AI action", output: `step_${current.length + 1}`, prompt: "" }]);
              setSelected(id);
            }}>+ Add step</button>
          </div>
        )}
      </div>

      {selectedBlock ? (
        <aside className="dio-inspector">
          <header><strong>{selectedBlock.kind}</strong><button type="button" aria-label="Close inspector" onClick={() => setSelected(null)}>×</button></header>
          <label>Step name<input value={selectedBlock.name} onChange={(event) => updateSelected({ name: event.target.value })} /></label>
          {selectedBlock.kind === "AI action" ? (
            <>
              <label>Prompt<textarea maxLength={50000} value={selectedBlock.prompt || ""} onChange={(event) => updateSelected({ prompt: event.target.value })} /></label>
              <span className="dio-character-count">{(selectedBlock.prompt || "").length.toLocaleString()} / 50,000</span>
              <div className="dio-inspector-card"><strong>Workflow context</strong><span>@contract_upload</span><span>@playbook_upload</span></div>
              <div className="dio-inspector-card"><strong>Embedded files</strong><span>No embedded files · maximum 50</span></div>
              <div className="dio-inspector-card"><strong>Knowledge sources</strong><span>Attach sources to this step only.</span></div>
              <label className="dio-toggle-line"><input type="checkbox" /> Deep analysis</label>
              <label>Model<select defaultValue="configured"><option value="configured">Workspace configured model</option></select></label>
            </>
          ) : (
            <div className="dio-inspector-card"><strong>{selectedBlock.kind === "Response" ? "Response content" : "Input requirements"}</strong><span>{selectedBlock.kind === "Response" ? "Select the earlier outputs presented to the user." : "Configure accepted files and whether this input is required."}</span></div>
          )}
          <label>Output label<input value={selectedBlock.output} onChange={(event) => updateSelected({ output: event.target.value })} /></label>
        </aside>
      ) : null}
    </section>
  );
}

export interface WorkflowRunRefusal {
  code?: string;
  message: string;
  requestedPlaybook?: string | null;
}

/**
 * A refused run is a legitimate answer from the server, not a failure: the run
 * was declined (for example because no playbook resolved) and nothing was
 * produced. It is reported in full so the reader can see why.
 */
export function workflowRunRefusal(result: any): WorkflowRunRefusal | null {
  if (!result) return null;
  const refusal = result.refusal || result.review?.refusal || null;
  if (!refusal && result.status !== "refused") return null;
  const message = refusal?.message || result.message
    || "This run was declined by the server and produced no result.";
  return {
    code: refusal?.code,
    message,
    requestedPlaybook: refusal?.requested_playbook ?? null,
  };
}

interface WorkflowOutputSection {
  heading?: string;
  items?: Array<{ label?: string; text?: string; evidence?: any[] }>;
}

/**
 * Collect the sections the server returned across whichever output packet this
 * workflow produced. Sections with no items are dropped rather than rendered as
 * an empty heading that reads like a finding was expected and lost.
 */
function workflowRunSections(result: any): WorkflowOutputSection[] {
  return [result?.assistant, result?.draft, result?.research]
    .filter(Boolean)
    .flatMap((packet: any) => (packet.sections || []) as WorkflowOutputSection[])
    .filter((section) => (section?.items || []).length > 0);
}

function tableCell(row: any, columnKey: string) {
  if (columnKey === "document") return { value: row?.document_name || row?.document_id || "Document", status: "" };
  if (columnKey === "review_status") return { value: row?.review_status || "", status: "" };
  if (columnKey === "highest_risk") return { value: row?.highest_risk || "", status: "" };
  if (columnKey === "next_action") return { value: row?.next_action || "", status: "" };
  const cell = row?.cells?.[columnKey];
  return { value: cell?.value ?? "", status: cell?.status || "" };
}

function evidenceLabel(item: any) {
  const name = item?.document_name || item?.filename || "";
  const snippet = item?.snippet || item?.quote || "";
  if (name && snippet) return `${name} — “${snippet}”`;
  return name || snippet;
}

export interface WorkflowRunOutputProps {
  result: any;
}

/**
 * Renders the workflow run response and nothing else.
 *
 * This screen previously printed a fixed two-row example table ("Liability cap",
 * "Assignment" and so on) on every successful run, whatever the server returned.
 * For a legal product that is a provenance failure: the reader cannot tell an
 * invented position from a found one. So every value below is read out of the
 * response, and when the response carries no results that is said plainly
 * instead of being filled in with an example.
 */
export function WorkflowRunOutput({ result }: WorkflowRunOutputProps) {
  const refusal = workflowRunRefusal(result);
  if (refusal) {
    return (
      <div className="dio-alert" role="alert">
        <strong>This workflow was not run.</strong>
        <p>{refusal.message}</p>
        {refusal.requestedPlaybook ? <p>Requested playbook: {refusal.requestedPlaybook}</p> : null}
      </div>
    );
  }

  const summary = result?.summary || null;
  const playbookName = result?.playbook?.name || "";
  const answer = typeof result?.assistant?.answer === "string" ? result.assistant.answer.trim() : "";
  const sections = workflowRunSections(result);
  const columns = (result?.table?.columns || []) as Array<{ key: string; label?: string }>;
  const tableRows = (result?.table?.rows || []) as any[];
  const hasTable = columns.length > 0 && tableRows.length > 0;
  const issues = (result?.research?.issues || []) as any[];
  const memoOutline = (result?.research?.memo_outline || []) as string[];
  const courtListener = result?.research?.courtlistener?.message || "";
  const reviewRows = (result?.review?.rows || []) as any[];
  const hasPrimaryContent = Boolean(answer) || sections.length > 0 || hasTable || issues.length > 0;
  const showReviewRows = !hasPrimaryContent && reviewRows.length > 0;

  if (!hasPrimaryContent && !showReviewRows) {
    return (
      <div className="dio-empty">
        <strong>This run returned no results.</strong>
        <span>
          The server completed the run without producing any findings, rows or drafting output.
          Nothing is shown here because nothing was returned. Check that the intended documents and
          playbook were attached, then run it again.
        </span>
      </div>
    );
  }

  return (
    <>
      <p className="dio-footnote">
        {playbookName
          ? `Playbook applied: ${playbookName}.`
          : "No playbook was recorded for this run."}
        {summary
          ? ` ${summary.documents ?? 0} document${(summary.documents ?? 0) === 1 ? "" : "s"}`
            + `, ${summary.found ?? 0} found, ${summary.gaps ?? 0} gap${(summary.gaps ?? 0) === 1 ? "" : "s"}`
            + `, ${summary.high_risk ?? 0} high risk.`
          : ""}
      </p>

      {answer
        ? answer.split(/\n{2,}/).filter(Boolean).map((paragraph: string, index: number) => (
          <p key={`answer-${index}`}>{paragraph}</p>
        ))
        : null}

      {sections.map((section, index) => (
        <div key={`${section.heading || "section"}-${index}`} className="dio-inspector-card">
          <strong>{section.heading || "Findings"}</strong>
          {(section.items || []).map((item, itemIndex) => (
            <span key={`${item.label || "item"}-${itemIndex}`}>
              {item.label ? <strong>{item.label}</strong> : null}
              {item.label && item.text ? " — " : ""}
              {item.text}
              {(item.evidence || []).filter((entry) => evidenceLabel(entry)).map((entry, entryIndex) => (
                <small key={`evidence-${entryIndex}`}>{evidenceLabel(entry)}</small>
              ))}
            </span>
          ))}
        </div>
      ))}

      {hasTable ? (
        <div className="dio-table-wrap">
          <table className="dio-table">
            <thead>
              <tr>{columns.map((column) => <th key={column.key}>{column.label || column.key}</th>)}</tr>
            </thead>
            <tbody>
              {tableRows.map((row, rowIndex) => (
                <tr key={row.document_version_id || row.document_id || `row-${rowIndex}`}>
                  {columns.map((column) => {
                    const cell = tableCell(row, column.key);
                    return (
                      <td key={column.key}>
                        {cell.value}
                        {cell.status ? <span className="dio-tag">{cell.status}</span> : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {issues.length ? (
        <div className="dio-table-wrap">
          <table className="dio-table">
            <thead><tr><th>Issue</th><th>Research question</th><th>Fact from the matter</th></tr></thead>
            <tbody>
              {issues.map((issue, index) => (
                <tr key={`${issue.issue || "issue"}-${index}`}>
                  <td>{issue.issue}</td>
                  <td>{issue.research_question}</td>
                  <td>{issue.matter_fact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {memoOutline.length ? (
        <div className="dio-inspector-card">
          <strong>Memorandum outline</strong>
          {memoOutline.map((entry, index) => <span key={`outline-${index}`}>{entry}</span>)}
        </div>
      ) : null}

      {courtListener ? <p className="dio-footnote">{courtListener}</p> : null}

      {showReviewRows ? (
        <div className="dio-table-wrap">
          <table className="dio-table">
            <thead><tr><th>Issue</th><th>Status</th><th>Risk</th><th>Finding</th><th>Action</th></tr></thead>
            <tbody>
              {reviewRows.map((row, index) => (
                <tr key={row.key || `review-${index}`}>
                  <td>{row.topic}</td>
                  <td><span className="dio-tag">{row.status === "found" ? "Found" : "Not found"}</span></td>
                  <td>{row.risk}</td>
                  <td>{row.finding}</td>
                  <td>{row.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

export interface WorkflowRunScreenProps {
  workflowName: string;
  playbookOptions?: Array<{ id: string; name: string }>;
  busy?: boolean;
  onBack: () => void;
  /**
   * `files` carries every document attached to the run, in the order they were
   * attached. `file` is the first of them and is kept only so existing callers
   * that read a single document keep compiling; new callers must read `files`.
   */
  onSubmit?: (input: { files: File[]; file: File; playbookId?: string; note?: string }) => Promise<any>;
  onOpenDraft?: () => void;
  onOpenTable?: () => void;
  onExport?: () => void;
}

export function WorkflowRunScreen({
  workflowName,
  playbookOptions = [],
  busy = false,
  onBack,
  onSubmit,
  onOpenDraft,
  onOpenTable,
  onExport,
}: WorkflowRunScreenProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [playbookId, setPlaybookId] = useState(playbookOptions[0]?.id || "");
  const [note, setNote] = useState("");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState("");

  const addFiles = (incoming: File[]) => {
    if (!incoming.length) return;
    setFiles((current) => mergeFileSelection(current, incoming));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!files.length || !onSubmit) return;
    setError("");
    try {
      setResult(await onSubmit({
        files,
        file: files[0],
        playbookId: playbookId || undefined,
        note: note || undefined,
      }));
    } catch (caught: any) {
      setError(caught?.message || "The workflow could not be completed.");
    }
  };

  if (result) {
    const refused = Boolean(workflowRunRefusal(result));
    return (
      <section className="dio-run dio-run--output">
        <button type="button" className="dio-back" onClick={onBack}>‹ All workflows</button>
        <span className="dio-kicker">{refused ? "Not run" : "Run finished"}</span>
        <h1>{workflowName}</h1>
        {refused ? null : (
          <p>Everything below is what the server returned for this run. Check each cited source before relying on it.</p>
        )}
        <WorkflowRunOutput result={result} />
        <div className="dio-output-actions">
          <button className="dio-btn dio-btn--primary" type="button" disabled={!onOpenDraft} onClick={onOpenDraft}>Open the redline</button>
          <button className="dio-btn" type="button" disabled={!onOpenTable} onClick={onOpenTable}>Open as a review table</button>
          <button className="dio-btn" type="button" disabled={!onExport} onClick={onExport}>Export to Word</button>
          <button className="dio-btn" type="button" onClick={() => { setResult(null); setFiles([]); }}>Run again</button>
        </div>
      </section>
    );
  }

  return (
    <section className="dio-run">
      <button type="button" className="dio-back" onClick={onBack}>‹ All workflows</button>
      <span className="dio-kicker">Step 1 of 5</span>
      <h1>{workflowName}</h1>
      <p>Provide the required inputs. The workflow keeps each source attached to the step that uses it.</p>
      <form onSubmit={(event) => { void submit(event); }}>
        <label className="dio-dropzone">
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".doc,.docx,.pdf"
            onChange={(event) => { addFiles(Array.from(event.target.files || [])); event.target.value = ""; }}
          />
          <strong>{files.length ? `${files.length} document${files.length === 1 ? "" : "s"} attached` : "Upload the contracts to review"}</strong>
          <span>.docx or .pdf · attach more than one document and each stays attached · documents near 100 pages may need section-level review</span>
          <button type="button" className="dio-btn" onClick={() => fileRef.current?.click()}>Browse</button>
        </label>
        {files.length ? (
          <ul className="dio-selected-files" aria-label="Attached documents">
            {files.map((item) => (
              <li key={fileSelectionKey(item)}>
                <span><strong>{item.name}</strong><small>{Math.max(1, Math.ceil(item.size / 1024)).toLocaleString()} KB</small></span>
                <button
                  type="button"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => setFiles((current) => current.filter((candidate) => candidate !== item))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <label>Playbook<select value={playbookId} onChange={(event) => setPlaybookId(event.target.value)}><option value="">No playbook selected</option>{playbookOptions.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label>Optional note<textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add instructions for this run" /></label>
        {error ? <div className="dio-alert" role="alert">{error}</div> : null}
        <div className="dio-form-actions"><span>Estimated runtime 3–5 minutes</span><button type="button" className="dio-btn" onClick={onBack}>Cancel</button><button type="submit" className="dio-btn dio-btn--primary" disabled={!files.length || !onSubmit || busy}>{busy ? "Running…" : "Send"}</button></div>
      </form>
    </section>
  );
}

export interface ProjectsScreenProps {
  projects: DocumentIntelligenceProject[];
  busy?: boolean;
  onNew: () => void;
  onOpen: (project: DocumentIntelligenceProject) => void;
  onDelete: (project: DocumentIntelligenceProject) => void;
}

export function ProjectsScreen({ projects, busy = false, onNew, onOpen, onDelete }: ProjectsScreenProps) {
  const [query, setQuery] = useState("");
  const visible = projects.filter((project) => `${project.name} ${project.description || ""}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <section className="dio-screen">
      <div className="dio-heading-row">
        <div><h1>Projects</h1><p>Keep files, sources, threads and generated outputs scoped to one legal matter.</p></div>
        <button type="button" className="dio-btn dio-btn--primary" onClick={onNew}>New project</button>
      </div>
      <label className="dio-search dio-search--wide"><span className="dio-sr-only">Search projects</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search projects" /></label>
      <div className="dio-table-wrap">
        <table className="dio-table">
          <thead><tr><th>Project</th><th>Matter</th><th>Files</th><th>Threads</th><th>Activity</th><th aria-label="Actions" /></tr></thead>
          <tbody>{visible.map((project) => (
            <tr key={project.id}>
              <td><button type="button" className="dio-link" onClick={() => onOpen(project)}>{project.name}</button><small>{project.description || "No project description"}</small></td>
              <td>{project.id.slice(0, 8).toUpperCase()}</td>
              <td>{project.versions?.length ?? 0}</td>
              <td>—</td>
              <td>{project.updated_at ? new Date(project.updated_at).toLocaleDateString() : "Created recently"}</td>
              <td>{project.can_delete ? <button type="button" className="dio-icon-btn" disabled={busy} aria-label={`Delete ${project.name}`} onClick={() => onDelete(project)}>×</button> : null}</td>
            </tr>
          ))}</tbody>
        </table>
        {!visible.length ? <div className="dio-empty"><strong>No projects found.</strong><span>Create a project to keep document work scoped to a matter.</span></div> : null}
      </div>
      <p className="dio-footnote">Deleting a project removes its Document Intelligence workspace. Canonical Repository documents remain in the Repository.</p>
    </section>
  );
}

export interface ProjectNewInput {
  name: string;
  description?: string;
  clientId?: number | null;
  files: File[];
  sourceIds: string[];
}

export interface ProjectNewScreenProps {
  clients?: Array<{ id: number; name: string }>;
  sources?: DocumentIntelligenceSourceMode[];
  busy?: boolean;
  onBack: () => void;
  onCreate: (input: ProjectNewInput) => Promise<void> | void;
}

export function ProjectNewScreen({ clients = [], sources = [], busy = false, onBack, onCreate }: ProjectNewScreenProps) {
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [draggingFiles, setDraggingFiles] = useState(false);
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  const sourceToggle = (key: string) => setSourceIds((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key].slice(-2));
  const addFiles = (incoming: File[]) => {
    setFiles((current) => mergeFileSelection(current, incoming));
  };
  const dropFiles = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDraggingFiles(false);
    addFiles(Array.from(event.dataTransfer.files || []));
  };

  return (
    <section className="dio-form-screen">
      <button type="button" className="dio-back" onClick={onBack}>‹ All projects</button>
      <span className="dio-kicker">New project</span>
      <h1>Create a document intelligence project</h1>
      <form onSubmit={(event) => {
        event.preventDefault();
        void onCreate({ name: name.trim(), description: description.trim() || undefined, clientId: clientId ? Number(clientId) : null, files, sourceIds });
      }}>
        <label>Project name<input required maxLength={200} value={name} onChange={(event) => setName(event.target.value)} placeholder="Project Larkspur" /></label>
        <label>Client or company<select value={clientId} onChange={(event) => setClientId(event.target.value)}><option value="">Assign automatically from the first client document</option>{clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label>
        <p className="dio-field-note">The project starts private. Adding a Repository or request document assigns its client automatically; documents belonging to a different client are blocked.</p>
        <label>Project number<input readOnly value="Generated automatically after project creation" /></label>
        <p className="dio-field-note">KontractAI assigns the project number when you select Create project.</p>
        <label>Purpose (optional)<textarea maxLength={2000} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
        <div className="dio-rule" />
        <label
          className={`dio-dropzone ${draggingFiles ? "is-dragging" : ""}`}
          onDragEnter={(event) => { event.preventDefault(); setDraggingFiles(true); }}
          onDragOver={(event) => { event.preventDefault(); setDraggingFiles(true); }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDraggingFiles(false);
          }}
          onDrop={dropFiles}
        >
          <input type="file" multiple onChange={(event) => { addFiles(Array.from(event.target.files || [])); event.target.value = ""; }} />
          <strong>{files.length ? `${files.length} file${files.length === 1 ? "" : "s"} selected` : "Add files to this project"}</strong>
          <span>Click to browse or drop up to 50 documents here. Repository documents can be added after creation.</span>
        </label>
        {files.length ? (
          <ul className="dio-selected-files" aria-label="Selected project files">
            {files.map((file) => (
              <li key={fileSelectionKey(file)}>
                <span><strong>{file.name}</strong><small>{Math.max(1, Math.ceil(file.size / 1024)).toLocaleString()} KB</small></span>
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => setFiles((current) => current.filter((item) => item !== file))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="dio-rule" />
        <fieldset><legend>Knowledge sources (up to two)</legend><div className="dio-chip-row">{sources.filter((source) => source.status === "available").map((source) => <button key={source.key} type="button" className={`dio-chip ${sourceIds.includes(source.key) ? "is-selected" : ""}`} onClick={() => sourceToggle(source.key)}>{source.label}</button>)}</div></fieldset>
        <div className="dio-form-actions"><button type="button" className="dio-btn" onClick={onBack}>Cancel</button><button type="submit" className="dio-btn dio-btn--primary" disabled={!name.trim() || busy}>{busy ? "Creating…" : "Create project"}</button></div>
      </form>
    </section>
  );
}

export interface ProjectDetailScreenProps {
  project: DocumentIntelligenceProject;
  threads?: DocumentIntelligenceThread[];
  busy?: boolean;
  onBack: () => void;
  onAddFiles?: (files: File[]) => Promise<void> | void;
  onNewConversation?: () => void;
  onNewTable?: () => void;
  onNewDraft?: () => void;
  onOpenThread?: (thread: DocumentIntelligenceThread) => void;
  onAsk?: (question: string) => Promise<any>;
}

export function ProjectDetailScreen({
  project,
  threads = [],
  busy = false,
  onBack,
  onAddFiles,
  onNewConversation,
  onNewTable,
  onNewDraft,
  onOpenThread,
  onAsk,
}: ProjectDetailScreenProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const ask = async () => {
    if (!onAsk || !question.trim()) return;
    const result = await onAsk(question.trim());
    setAnswer(result?.answer || "The project assistant completed the request. Open its citations before relying on the result.");
  };
  const docs = project.versions || [];
  return (
    <section className="dio-project-detail">
      <div className="dio-project-detail__main">
        <button type="button" className="dio-back" onClick={onBack}>‹ All projects</button>
        <div className="dio-stat-row">
          <div><strong>{docs.length}</strong><span>Files selected</span></div>
          <div><strong>—</strong><span>Sources enabled</span></div>
          <div><strong>{threads.length}</strong><span>Threads</span></div>
          <div><strong>—</strong><span>Outputs</span></div>
        </div>
        <div className="dio-section-heading"><div><h2>Documents in this project</h2><p>Selected files are available to every project-scoped task.</p></div><input ref={inputRef} hidden type="file" multiple onChange={(event) => { const next = Array.from(event.target.files || []); if (next.length) void onAddFiles?.(next); event.target.value = ""; }} /><button type="button" className="dio-btn dio-btn--primary" disabled={!onAddFiles || busy} onClick={() => inputRef.current?.click()}>Add files</button></div>
        <div className="dio-table-wrap"><table className="dio-table"><thead><tr><th>Document</th><th>Type</th><th>Version</th><th>Added</th></tr></thead><tbody>{docs.map((doc) => <tr key={doc.version_id}><td>{doc.filename || "Project document"}</td><td>{doc.file_type || "Document"}</td><td>v{doc.version_number || 1}</td><td>{doc.added_at ? new Date(doc.added_at).toLocaleDateString() : "—"}</td></tr>)}</tbody></table>{!docs.length ? <div className="dio-empty"><strong>No documents yet.</strong><span>Add files from your computer or the Contract Repository.</span></div> : null}</div>
        <div className="dio-section-heading"><div><h2>Threads</h2><p>Conversations, review tables and drafts remain scoped to this project.</p></div><div><button type="button" className="dio-btn" disabled={!onNewConversation} onClick={onNewConversation}>New conversation</button><button type="button" className="dio-btn" disabled={!onNewTable} onClick={onNewTable}>New review table</button><button type="button" className="dio-btn" disabled={!onNewDraft} onClick={onNewDraft}>New draft</button></div></div>
        <div className="dio-thread-list">{threads.map((thread) => <button key={thread.id} type="button" onClick={() => onOpenThread?.(thread)}><span className="dio-tag">Conversation</span><strong>{thread.title}</strong><small>{thread.status || `${thread.message_count || 0} messages`}</small><span>›</span></button>)}{!threads.length ? <div className="dio-empty"><strong>No threads yet.</strong><span>Start a conversation, table or draft for this matter.</span></div> : null}</div>
      </div>
      <aside className="dio-project-chat">
        <span className="dio-kicker">Project assistant</span>
        <h2>Ask across this project</h2>
        <p>The answer is limited to the project’s exact document versions and carries citations.</p>
        {answer ? <div className="dio-chat-answer">{answer}</div> : null}
        <textarea value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="Ask about the selected documents…" />
        <button type="button" className="dio-btn dio-btn--primary" disabled={!onAsk || !question.trim() || busy} onClick={() => { void ask(); }}>Send</button>
      </aside>
    </section>
  );
}

export interface HistoryScreenProps {
  items: DocumentIntelligenceHistoryItem[];
  onOpen: (item: DocumentIntelligenceHistoryItem) => void;
}

export function HistoryScreen({ items, onOpen }: HistoryScreenProps) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [group, setGroup] = useState<"day" | "matter">("day");
  const [favourites, setFavourites] = useState<Record<string, boolean>>({});
  const visible = items.filter((item) => (type === "all" || item.type === type) && `${item.title} ${item.workspace_name || ""}`.toLowerCase().includes(query.toLowerCase()));
  const groups = visible.reduce<Record<string, DocumentIntelligenceHistoryItem[]>>((current, item) => {
    const key = group === "matter" ? item.workspace_name || "No matter" : new Date(item.updated_at || item.created_at || Date.now()).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    (current[key] ||= []).push(item);
    return current;
  }, {});
  return (
    <section className="dio-screen">
      <div className="dio-heading-row"><div><h1>History</h1><p>Search, group and reopen completed Document Intelligence work.</p></div></div>
      <div className="dio-filterbar">
        <label className="dio-search"><span className="dio-sr-only">Search history</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search history" /></label>
        <select aria-label="Task type" value={type} onChange={(event) => setType(event.target.value)}><option value="all">All task types</option><option value="thread">Threads</option><option value="review_table">Review tables</option><option value="redline">Redlines</option><option value="workflow">Workflows</option></select>
        <div className="dio-segment" aria-label="Group history"><button type="button" className={group === "day" ? "is-active" : ""} onClick={() => setGroup("day")}>Day</button><button type="button" className={group === "matter" ? "is-active" : ""} onClick={() => setGroup("matter")}>Matter</button></div>
      </div>
      <div className="dio-history-groups">{Object.entries(groups).map(([label, rows]) => <section key={label}><h2>{label}<span>{rows.length}</span></h2>{rows.map((item) => <div className="dio-history-row" key={`${item.type}:${item.id}`}><button type="button" aria-label={`Favourite ${item.title}`} aria-pressed={!!favourites[item.id]} onClick={() => setFavourites((current) => ({ ...current, [item.id]: !current[item.id] }))}>{favourites[item.id] ? "★" : "☆"}</button><button type="button" onClick={() => onOpen(item)}><strong>{item.title}</strong><small><span className="dio-tag">{item.type.replace("_", " ")}</span>{item.source_count ? `${item.source_count} documents` : ""} · {item.workspace_name || "No matter"} · {new Date(item.updated_at || item.created_at || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</small></button></div>)}</section>)}{!visible.length ? <div className="dio-empty"><strong>No history matches these filters.</strong><span>Completed threads and outputs appear here subject to retention policy.</span></div> : null}</div>
      <p className="dio-footnote">Favourites remain subject to the workspace retention policy.</p>
    </section>
  );
}

export interface SettingsScreenProps {
  sources: DocumentIntelligenceSourceMode[];
  busy?: boolean;
  onSourceToggle?: (source: DocumentIntelligenceSourceMode, enabled: boolean) => Promise<void> | void;
  onCreateFormat?: (file: File, visibility: "workspace" | "private") => Promise<void> | void;
  onRetentionSave?: (input: { period: string; matterNumberPolicy: string; sharing: string }) => Promise<void> | void;
}

export function SettingsScreen({ sources, busy = false, onSourceToggle, onCreateFormat, onRetentionSave }: SettingsScreenProps) {
  const [tab, setTab] = useState<"knowledge" | "export" | "retention">("knowledge");
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [formatOpen, setFormatOpen] = useState(false);
  const [formatFile, setFormatFile] = useState<File | null>(null);
  const [formatVisibility, setFormatVisibility] = useState<"workspace" | "private">("workspace");
  const [period, setPeriod] = useState("18 months");
  const [matterPolicy, setMatterPolicy] = useState("Optional");
  const [sharing, setSharing] = useState("Workspace");
  return (
    <section className="dio-settings">
      <aside>
        {([["knowledge", "Knowledge"], ["export", "Export formats"], ["retention", "History and retention"]] as const).map(([key, label]) => <button key={key} type="button" className={tab === key ? "is-active" : ""} onClick={() => setTab(key)}>{label}</button>)}
      </aside>
      <div className="dio-settings__content">
        {tab === "knowledge" ? (
          <>
            <span className="dio-kicker">Workspace settings</span><h1>Knowledge</h1><p>Control which public and configured sources are available to Document Intelligence.</p>
            <div className="dio-table-wrap"><table className="dio-table"><thead><tr><th>Source</th><th>Coverage</th><th>Processing region</th><th>Enabled</th></tr></thead><tbody>{sources.map((source) => {
              const enabled = overrides[source.key] ?? source.status === "available";
              return <tr key={source.key}><td>{source.label}</td><td>{source.detail || source.key.replace(/_/g, " ")}</td><td>{source.key === "web_search" ? "United States" : "Workspace region"}</td><td><button type="button" role="switch" aria-checked={enabled} className={`dio-switch ${enabled ? "is-on" : ""}`} disabled={!onSourceToggle || busy} title={!onSourceToggle ? "Workspace source administration is not configured." : undefined} onClick={() => { const next = !enabled; setOverrides((current) => ({ ...current, [source.key]: next })); void onSourceToggle?.(source, next); }}><span /></button></td></tr>;
            })}</tbody></table>{!sources.length ? <div className="dio-empty"><strong>No knowledge sources are configured.</strong><span>Configure a provider before making it available to the workspace.</span></div> : null}</div>
            <div className="dio-privacy-note">Provider-supported retrieval uses a derived, generalised query. It must not send the verbatim prompt or project documents, and provider query data is deleted after results return.</div>
          </>
        ) : null}
        {tab === "export" ? (
          <>
            <div className="dio-heading-row"><div><span className="dio-kicker">Workspace settings</span><h1>Export formats</h1><p>Apply approved Word styles without retaining substantive content from the template.</p></div><button type="button" className="dio-btn dio-btn--primary" disabled={!onCreateFormat} title={!onCreateFormat ? "Export-format administration is not configured." : undefined} onClick={() => setFormatOpen(true)}>Create format</button></div>
            <div className="dio-table-wrap"><table className="dio-table"><thead><tr><th>Format</th><th>Source file</th><th>Styles mapped</th><th>Visibility</th></tr></thead><tbody><tr><td colSpan={4}>No export formats have been returned by the server.</td></tr></tbody></table></div>
            <p className="dio-footnote">Unmapped styles are ignored when the format is applied.</p>
          </>
        ) : null}
        {tab === "retention" ? (
          <>
            <span className="dio-kicker">Workspace settings</span><h1>History and retention</h1><p>These controls apply to completed Document Intelligence threads and shared work.</p>
            <form className="dio-retention" onSubmit={(event) => { event.preventDefault(); void onRetentionSave?.({ period, matterNumberPolicy: matterPolicy, sharing }); }}>
              <label>Retention period<select value={period} onChange={(event) => setPeriod(event.target.value)}><option>6 months</option><option>12 months</option><option>18 months</option><option>24 months</option></select></label>
              <label>Client-matter-number policy<select value={matterPolicy} onChange={(event) => setMatterPolicy(event.target.value)}><option>Optional</option><option>Required for new work</option><option>Hidden</option></select></label>
              <fieldset><legend>Default sharing</legend>{["Private", "Project", "Workspace"].map((value) => <label key={value} className="dio-radio"><input type="radio" name="sharing" checked={sharing === value} onChange={() => setSharing(value)} />{value}</label>)}</fieldset>
              <button type="submit" className="dio-btn dio-btn--primary" disabled={!onRetentionSave || busy} title={!onRetentionSave ? "Retention administration is not configured." : undefined}>Save settings</button>
            </form>
          </>
        ) : null}
      </div>
      {formatOpen ? <div className="dio-dialog-backdrop"><div className="dio-dialog" role="dialog" aria-modal="true" aria-labelledby="dio-format-title"><header><h2 id="dio-format-title">Create export format</h2><button type="button" aria-label="Close" onClick={() => setFormatOpen(false)}>×</button></header><div><label>Word template<input type="file" accept=".dotx,.docx" onChange={(event) => setFormatFile(event.target.files?.[0] || null)} /></label><fieldset><legend>Visibility</legend><label className="dio-radio"><input type="radio" checked={formatVisibility === "workspace"} onChange={() => setFormatVisibility("workspace")} />Workspace</label><label className="dio-radio"><input type="radio" checked={formatVisibility === "private"} onChange={() => setFormatVisibility("private")} />Private</label></fieldset><p>KontractAI removes substantive content and maps the template’s styles.</p></div><footer><button type="button" className="dio-btn" onClick={() => setFormatOpen(false)}>Cancel</button><button type="button" className="dio-btn dio-btn--primary" disabled={!formatFile || busy} onClick={() => { if (formatFile) void onCreateFormat?.(formatFile, formatVisibility); setFormatOpen(false); }}>Create format</button></footer></div></div> : null}
    </section>
  );
}
